/**
 * Basket discount form.
 *
 * Applies voucher codes to the REAL Shopify cart via the Ajax Cart API
 * (POST /cart/update.js with a `discount` parameter, supported since 2025-05-21).
 *
 * There is no shadow cart and no checkout-button interception. Dawn's own
 * button[name="checkout"] submits the real cart form untouched, so this component
 * does not collide with cart/upsell apps.
 *
 * Server-side validation is UNCHANGED and mandatory. Every apply goes through the
 * app proxy first, because that is where Tesco Clubcard codes are minted on the fly
 * and where printed-trail codes have their variant entitlement set against the
 * current basket. A code may not exist in Shopify until the proxy has responded,
 * so the ordering below is load-bearing:
 *
 *   GET /cart.js  ->  POST /apps/hub/checkout/discount  ->  POST /cart/update.js
 */

const TT_DISCOUNT_PROXY_ENDPOINT = '/apps/hub/checkout/discount';
const TT_DISCOUNT_REQUEST_TIMEOUT = 30000;
const TT_DISCOUNT_WAITING_NOTICE_AT = 3000;
const TT_DISCOUNT_MESSAGE_MAX = 160;
const TT_DISCOUNT_MESSAGE_TIMEOUT = 5000;
const TT_DISCOUNT_MESSAGE_FADE = 400;
const TT_DISCOUNT_MIGRATION_FLAG = 'tt_discount_migrated_v1';
const TT_DISCOUNT_LEGACY_STORAGE = ['pending_gift_card', 'candyrack-discount-code'];

const TT_DISCOUNT_OUTCOME = {
  DISCOUNT: 'discount',
  GIFT_CARD: 'gift_card',
  INACTIVE: 'inactive',
  NOT_FOUND: 'not_found',
};

class TTDiscountForm extends HTMLElement {
  #controller = null;
  #pending = false;
  #waitingTimer = null;
  #messageTimer = null;
  #defaultSubmitLabel = '';
  #unsubscribeCartUpdate = null;

  /**
   * The codes we last successfully wrote. Authoritative for removal, because
   * /cart/update.js takes codes and the cart only reports application titles when
   * cart.discount_codes is unavailable. Null until the first write this page load.
   */
  #writtenCodes = null;

  connectedCallback() {
    if (this.#controller) return;

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#purgeLegacyStorage();

    this.form = this.querySelector('#tt-discount-form');
    this.input = this.querySelector('#tt-discount-input');
    this.submitButton = this.querySelector('#tt-discount-submit');
    this.submitLabel = this.querySelector('.tt-discount__submit-label');
    this.statusEl = this.querySelector('#tt-discount-status');
    this.errorEl = this.querySelector('#tt-discount-error');
    this.appliedEl = this.querySelector('#tt-discount-applied');
    this.clearButton = this.querySelector('[data-tt-discount-clear]');
    this.panel = this.querySelector('#tt-discount-panel');
    this.toggle = this.querySelector('[data-tt-discount-toggle]');

    if (!this.form || !this.input || !this.panel || !this.toggle) return;

    this.#defaultSubmitLabel = this.submitLabel ? this.submitLabel.textContent : 'Apply';

    this.form.addEventListener('submit', this.#onSubmit.bind(this), { signal });
    this.addEventListener('click', this.#onClick.bind(this), { signal });

    // Keep the applied-code list truthful when another component changes the cart -
    // removing the last line item drops its discount applications, and cart apps can
    // add upsell lines behind our back.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.#unsubscribeCartUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (!event || event.source === 'tt-discount-form') return;
        if (event.cartData && event.cartData.items) {
          this.#renderChips(this.#appliedFromCart(event.cartData));
        }
      });
    }
  }

  disconnectedCallback() {
    if (this.#controller) this.#controller.abort();
    this.#controller = null;
    if (this.#unsubscribeCartUpdate) this.#unsubscribeCartUpdate();
    this.#unsubscribeCartUpdate = null;
    this.#clearWaitingTimer();
    this.#clearMessageTimer();
  }

  /* ----------------------------------------------------------------------- *
   * Public actions
   * ----------------------------------------------------------------------- */

  /**
   * Validate a code server-side (minting it if necessary), then write it to the real cart.
   */
  async applyCode(rawCode) {
    const strings = this.#strings;
    const code = (rawCode || '').trim();

    this.#clearMessages();

    if (!code) {
      this.#showError(strings.empty);
      return;
    }

    this.#setPending(true, strings.checking);

    try {
      const cart = await this.#fetchJson(this.#route('cart.js'));

      // Derived from the cart we just read, not from the rendered chips, so codes
      // already on the basket survive the write.
      const current = await this.#authoritativeCodes(cart);

      if (current.length >= this.#maxCodes) {
        this.#showError(strings.max);
        return;
      }

      if (current.some((applied) => applied.toLowerCase() === code.toLowerCase())) {
        this.#showStatus(strings.duplicate);
        return;
      }

      // Must complete before the cart write - a Clubcard code does not exist in
      // Shopify until this call has created it.
      const proxyResponse = await this.#validateWithProxy(cart, code);
      const result = this.#classifyProxyResponse(proxyResponse, code);

      if (result.outcome === TT_DISCOUNT_OUTCOME.GIFT_CARD) {
        this.#showStatus(result.message);
        return;
      }

      if (result.outcome !== TT_DISCOUNT_OUTCOME.DISCOUNT) {
        this.#showError(result.message);
        return;
      }

      // Re-read immediately before the write, so a code applied since our first read -
      // by OptiMonk, or another tab - is carried through rather than wiped.
      const before = await this.#authoritativeCodes();
      const next = this.#dedupe([...before, ...current, result.code]);
      const updated = await this.#writeCartDiscountsSafely(next, before);

      if (this.#wasRejected(updated, result.code)) {
        // Shopify accepted the write but flagged the code as not applicable - a
        // combinesWith rule against a code already on the basket.
        this.#writtenCodes = this.#dedupe(next.filter((code) => code.toLowerCase() !== result.code.toLowerCase()));
        await this.#renderFromCart(updated);
        this.#publishCartUpdate(updated);
        this.#showError(strings.combine);
        return;
      }

      this.#writtenCodes = next;
      await this.#renderFromCart(updated);
      this.#publishCartUpdate(updated);

      this.input.value = '';
      this.#showStatus(`${result.code} ${strings.applied}`);
    } catch (error) {
      this.#reportFailure('apply', error);
    } finally {
      this.#setPending(false);
    }
  }

  /**
   * Remove one code. No proxy call - removal never needs to mint anything.
   */
  async removeCode(code) {
    const current = await this.#authoritativeCodes();
    const next = current.filter((applied) => applied.toLowerCase() !== String(code).toLowerCase());
    if (next.length === current.length) return;
    await this.#writeCodes(next);
  }

  /**
   * Remove every code. An empty string is the documented "remove all discounts" form.
   */
  async clearCodes() {
    await this.#writeCodes([]);
  }

  /* ----------------------------------------------------------------------- *
   * Event handlers - scoped to this element, never to document
   * ----------------------------------------------------------------------- */

  #onSubmit(event) {
    event.preventDefault();
    if (this.#pending) return;
    this.applyCode(this.input.value);
  }

  #onClick(event) {
    if (!(event.target instanceof Element)) return;

    const toggle = event.target.closest('[data-tt-discount-toggle]');
    if (toggle && this.contains(toggle)) {
      event.preventDefault();
      this.#togglePanel();
      return;
    }

    if (this.#pending) return;

    const remove = event.target.closest('[data-tt-discount-remove]');
    if (remove && this.contains(remove)) {
      event.preventDefault();
      this.removeCode(remove.dataset.ttDiscountRemove);
      return;
    }

    const clear = event.target.closest('[data-tt-discount-clear]');
    if (clear && this.contains(clear)) {
      event.preventDefault();
      this.clearCodes();
    }
  }

  /* ----------------------------------------------------------------------- *
   * Network
   * ----------------------------------------------------------------------- */

  /**
   * POST the raw, unmodified /cart.js payload. Two fields inside it are load-bearing
   * server-side and must not be stripped or reshaped:
   *   cart.token             - used as the Tesco transaction id; without it the whole
   *                            Clubcard mint path is skipped and codes silently fail
   *   cart.items[].variant_id / .product_id
   *                          - used to set printed-code variant entitlement against
   *                            this basket
   */
  #validateWithProxy(cart, code) {
    return this.#fetchJson(TT_DISCOUNT_PROXY_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ cart, discount_code: code }),
    });
  }

  /**
   * Write the full code list to the real cart. Always the FULL list - the `discount`
   * parameter sets the cart's whole code set rather than appending to it.
   */
  /**
   * Note: we deliberately do NOT ask for `sections` here.
   *
   * Shopify applies the discount to the cart correctly - the JSON comes back with
   * total_discount set and line_level_discount_allocations populated - but the section
   * HTML bundled into the same response is rendered WITHOUT the discount. Using it
   * paints correct data with stale markup, which is why the cart only showed the
   * discount after a manual refresh. Sections are fetched separately in
   * #fetchSections() once the write has landed.
   */
  /** The codes Shopify reports as being on the cart right now. */
  #codesOnCart(cart) {
    if (!cart || !Array.isArray(cart.discount_codes)) return [];
    return this.#dedupe(cart.discount_codes.filter((entry) => entry && entry.code).map((entry) => entry.code));
  }

  /**
   * Writes the codes, then checks nothing went missing and puts back anything that did.
   *
   * Reading the cart before the write is not enough on its own. A code can land on the
   * basket between our read and our write - OptiMonk applies its campaign coupon
   * automatically, so this is a real race, not a theoretical one - and because
   * /cart/update.js replaces the whole set, that code is wiped by our write.
   *
   * So we compare what was on the cart beforehand against what is there afterwards and
   * restore anything that disappeared. One retry only: if a code still will not stick
   * it is genuinely being rejected by Shopify, and hammering it would loop.
   *
   * Deliberately NOT reconciled against the rendered chips. On a fresh page load those
   * hold discount_application.title, which for a price-rule-backed discount is the rule
   * name rather than the code - it would never match, so we would re-post it forever.
   */
  async #writeCartDiscountsSafely(next, before) {
    let updated = await this.#writeCartDiscounts(next);

    const after = this.#codesOnCart(updated);
    const missing = before.filter((code) => !after.some((present) => present.toLowerCase() === code.toLowerCase()));

    if (missing.length === 0) return updated;

    console.warn(`[tt-discount-form] the write dropped ${missing.join(', ')} - restoring`);

    updated = await this.#writeCartDiscounts(this.#dedupe([...after, ...missing]));

    const restored = this.#codesOnCart(updated);
    const stillMissing = missing.filter(
      (code) => !restored.some((present) => present.toLowerCase() === code.toLowerCase())
    );

    if (stillMissing.length > 0) {
      console.warn(`[tt-discount-form] Shopify will not accept ${stillMissing.join(', ')} alongside the others`);
    }

    return updated;
  }

  #writeCartDiscounts(codes) {
    return this.#fetchJson(this.#route('cart/update.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: codes.join(',') }),
    });
  }

  /**
   * Fetches the cart page as a FULL DOCUMENT and returns it parsed.
   *
   * Shopify only computes discount allocations on a full document render. Verified on
   * live with a discount applied:
   *   /cart/update.js bundled sections  -> no discount markup
   *   /cart?sections=cart-items,cart-footer -> no discount markup
   *   GET /cart (full document)         -> discount markup present
   * So neither the bundled sections nor the Section Rendering API can be used here,
   * and this is the only render path that reflects the discount without navigating.
   */
  async #fetchFreshDocument() {
    try {
      const response = await fetch(window.location.pathname, {
        cache: 'no-store',
        credentials: 'same-origin',
      });

      if (!response.ok) throw new Error(`Request failed with status ${response.status}`);

      return new DOMParser().parseFromString(await response.text(), 'text/html');
    } catch (error) {
      console.warn('[tt-discount-form] could not re-render cart sections', error);
      return null;
    }
  }

  async #writeCodes(next) {
    this.#clearMessages();
    this.#setPending(true, this.#strings.removing);

    try {
      // Everything in `next` is meant to survive, so reconcile against it. A removal
      // has already been filtered out of the list, so it is not restored.
      const updated = await this.#writeCartDiscountsSafely(next, next);
      this.#writtenCodes = next;
      await this.#renderFromCart(updated);
      this.#publishCartUpdate(updated);
    } catch (error) {
      this.#reportFailure('update', error);
    } finally {
      this.#setPending(false);
    }
  }

  /**
   * Any non-2xx, and any 2xx whose body will not parse as JSON, is a transport error.
   * The proxy has a reachable branch that returns HTTP 500 with a non-JSON body, so
   * this must not assume a parseable envelope.
   */
  async #fetchJson(url, options = {}) {
    const controller = new AbortController();
    const timer = setTimeout(() => {
      controller.ttTimedOut = true;
      controller.abort();
    }, TT_DISCOUNT_REQUEST_TIMEOUT);

    try {
      const response = await fetch(url, { ...options, signal: controller.signal });
      const text = await response.text();

      let json = null;
      try {
        json = JSON.parse(text);
      } catch (parseError) {
        json = null;
      }

      if (!response.ok || json === null) {
        const error = new Error(`Request to ${url} failed with status ${response.status}`);
        error.status = response.status;
        error.body = text.slice(0, 500);
        throw error;
      }

      return json;
    } catch (error) {
      if (controller.ttTimedOut) error.ttTimeout = true;
      throw error;
    } finally {
      clearTimeout(timer);
    }
  }

  /* ----------------------------------------------------------------------- *
   * Response classification - pure, no side effects
   * ----------------------------------------------------------------------- */

  /**
   * The proxy returns HTTP 200 on both success and rejection, so the outcome is
   * decided entirely by the shape of discount_code_data. The four shapes it can
   * take do not share keys, hence the ordering here.
   *
   * free_delivery and free_delivery_code are deliberately ignored: that behaviour
   * was intentionally disabled and is out of scope.
   */
  #classifyProxyResponse(json, submittedCode) {
    const strings = this.#strings;
    const data = json && json.discount_code_data;

    if (!data) {
      return { outcome: TT_DISCOUNT_OUTCOME.NOT_FOUND, code: submittedCode, message: strings.unknown };
    }

    if (data.type === 'gift_card' && data.valid) {
      const balance = this.#formatMoney(data.balance, data.currency);
      return {
        outcome: TT_DISCOUNT_OUTCOME.GIFT_CARD,
        code: submittedCode,
        message: balance ? strings.giftCard.replace('{balance}', balance) : strings.giftCardPlain,
      };
    }

    if (data.exists === true && data.active === true) {
      // Prefer Shopify's canonical casing. `details` is absent on one branch, so
      // never assume it exists.
      const canonical = (data.details && data.details.code) || submittedCode;
      return { outcome: TT_DISCOUNT_OUTCOME.DISCOUNT, code: canonical, message: '' };
    }

    if (data.exists === true && data.active === false) {
      // The only case where the server message is customer-useful ("expired",
      // "usage limit reached"). Every other branch can interpolate raw exception
      // text, so those get a fixed string instead.
      const message =
        typeof data.message === 'string' && data.message.trim()
          ? data.message.trim().slice(0, TT_DISCOUNT_MESSAGE_MAX)
          : strings.unknown;
      return { outcome: TT_DISCOUNT_OUTCOME.INACTIVE, code: submittedCode, message };
    }

    if (data.message) console.info('[tt-discount-form] code rejected:', data.message);
    return { outcome: TT_DISCOUNT_OUTCOME.NOT_FOUND, code: submittedCode, message: strings.unknown };
  }

  /* ----------------------------------------------------------------------- *
   * Rendering
   * ----------------------------------------------------------------------- */

  /**
   * The codes currently on the cart.
   *
   * Prefer cart.discount_codes when Shopify returns it, because those are the actual
   * code strings - which is what /cart/update.js needs back on removal.
   *
   * The fallback unions cart-level and line-level applications (Tesco Clubcard and
   * printed codes are line-item scoped and never appear in
   * cart_level_discount_applications). That fallback yields discount_application.title,
   * which for a price-rule-backed discount is the RULE NAME, not the code - so it is
   * display-only and must never be treated as authoritative.
   */
  #appliedFromCart(cart) {
    if (Array.isArray(cart.discount_codes) && cart.discount_codes.length > 0) {
      return this.#dedupe(
        cart.discount_codes.filter((entry) => entry && entry.code && entry.applicable !== false).map((entry) => entry.code)
      );
    }

    return this.#applicationTitlesFromCart(cart);
  }

  #applicationTitlesFromCart(cart) {
    const codes = [];

    (cart.cart_level_discount_applications || []).forEach((application) => {
      if (application && application.type === 'discount_code' && application.title) {
        codes.push(application.title);
      }
    });

    (cart.items || []).forEach((item) => {
      (item.line_level_discount_allocations || []).forEach((allocation) => {
        const application = allocation && allocation.discount_application;
        if (application && application.type === 'discount_code' && application.title) {
          codes.push(application.title);
        }
      });
    });

    return this.#dedupe(codes);
  }

  /** Case-insensitive dedupe - the same discount can surface at cart and line level. */
  #dedupe(values) {
    const seen = new Set();
    return values.filter((value) => {
      const key = value.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  /**
   * Only treat a write as rejected when Shopify says so explicitly. We cannot infer
   * rejection from a missing title match, because titles are not codes.
   */
  #wasRejected(cart, code) {
    if (!Array.isArray(cart.discount_codes)) return false;
    return cart.discount_codes.some(
      (entry) => entry && entry.code && entry.code.toLowerCase() === code.toLowerCase() && entry.applicable === false
    );
  }

  #sectionsToRender() {
    const sections = [];
    const items = document.getElementById('main-cart-items');
    const footer = document.getElementById('main-cart-footer');

    if (items) sections.push({ id: 'main-cart-items', section: items.dataset.id, selector: '.js-contents' });
    if (footer) sections.push({ id: 'main-cart-footer', section: footer.dataset.id, selector: '.js-contents' });
    sections.push({ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' });
    sections.push({ id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' });

    return sections.filter((section) => section.section);
  }

  async #renderFromCart(cart) {
    const fresh = await this.#fetchFreshDocument();

    if (fresh) {
      this.#sectionsToRender().forEach((section) => {
        const container = document.getElementById(section.id);
        const sourceContainer = fresh.getElementById(section.id);
        if (!container || !sourceContainer) return;

        const target = container.querySelector(section.selector) || container;

        // main-cart-footer holds several .js-contents blocks and querySelector takes
        // the first (the subtotal), which is what we want. Guard anyway so a future
        // block reorder can never make us tear out our own element mid-request.
        if (target.contains(this)) return;

        const source = sourceContainer.querySelector(section.selector) || sourceContainer;
        target.innerHTML = source.innerHTML;
      });
    }

    // cart.discount_codes carries real codes, so it supersedes anything we tracked.
    // Without it we keep showing the codes we wrote, since application titles are
    // rule names and cannot be posted back on removal.
    if (Array.isArray(cart.discount_codes)) {
      this.#writtenCodes = this.#appliedFromCart(cart);
    }

    this.#renderChips(this.#writtenCodes || this.#appliedFromCart(cart));

    const footer = document.getElementById('main-cart-footer');
    if (footer) footer.classList.toggle('is-empty', cart.item_count === 0);
  }

  #renderChips(codes) {
    if (!this.appliedEl) return;

    this.appliedEl.replaceChildren();

    codes.forEach((code) => {
      const item = document.createElement('li');
      item.className = 'tt-discount__chip';
      item.dataset.ttCode = code;

      const label = document.createElement('span');
      label.className = 'tt-discount__chip-label';
      label.textContent = code;

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'tt-discount__remove';
      remove.dataset.ttDiscountRemove = code;
      remove.setAttribute('aria-label', `Remove voucher code ${code}`);

      const glyph = document.createElement('span');
      glyph.setAttribute('aria-hidden', 'true');
      glyph.textContent = '×';
      remove.appendChild(glyph);

      item.append(label, remove);
      this.appliedEl.appendChild(item);
    });

    if (this.clearButton) this.clearButton.hidden = codes.length < 2;
    if (codes.length > 0) this.#togglePanel(true);
  }

  #togglePanel(force) {
    const open = typeof force === 'boolean' ? force : this.toggle.getAttribute('aria-expanded') !== 'true';
    this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    this.panel.hidden = !open;
    this.classList.toggle('tt-discount--open', open);
  }

  /* ----------------------------------------------------------------------- *
   * Messaging and pending state
   * ----------------------------------------------------------------------- */

  #showStatus(text) {
    this.#showMessage(this.statusEl, text);
  }

  #showError(text) {
    this.#showMessage(this.errorEl, text);
  }

  /**
   * Messages auto-dismiss after 5s, sliding and fading out, then clear themselves.
   * Setting textContent on an empty live region is what triggers the announcement,
   * so clearing first is deliberate.
   */
  #showMessage(element, text) {
    this.#clearMessages();
    if (!element || !text) return;

    element.textContent = text;

    this.#messageTimer = setTimeout(() => {
      element.classList.add('tt-discount__message--hiding');

      this.#messageTimer = setTimeout(() => {
        element.textContent = '';
        element.classList.remove('tt-discount__message--hiding');
        this.#messageTimer = null;
      }, TT_DISCOUNT_MESSAGE_FADE);
    }, TT_DISCOUNT_MESSAGE_TIMEOUT);
  }

  #clearMessages() {
    this.#clearMessageTimer();

    [this.statusEl, this.errorEl].forEach((element) => {
      if (!element) return;
      element.textContent = '';
      element.classList.remove('tt-discount__message--hiding');
    });
  }

  #clearMessageTimer() {
    if (this.#messageTimer) clearTimeout(this.#messageTimer);
    this.#messageTimer = null;
  }

  #reportFailure(stage, error) {
    console.error(`[tt-discount-form] ${stage} failed`, error);
    this.#showError(error && error.ttTimeout ? this.#strings.timeout : this.#strings.error);
  }

  /**
   * The mint path can spend ~12s in server-side sleeps before responding, so the
   * pending state has to be real. Always called from a `finally` so it cannot leak.
   */
  #setPending(on, label) {
    this.#pending = on;
    this.setAttribute('aria-busy', on ? 'true' : 'false');

    if (this.input) this.input.disabled = on;
    if (this.submitButton) this.submitButton.disabled = on;
    this.querySelectorAll('[data-tt-discount-remove], [data-tt-discount-clear]').forEach((button) => {
      button.disabled = on;
    });

    if (this.submitLabel) {
      this.submitLabel.textContent = on ? label || this.#strings.checking : this.#defaultSubmitLabel;
    }

    this.#clearWaitingTimer();

    if (on) {
      this.#waitingTimer = setTimeout(() => {
        if (this.#pending && this.submitLabel) this.submitLabel.textContent = this.#strings.waiting;
      }, TT_DISCOUNT_WAITING_NOTICE_AT);
    }
  }

  #clearWaitingTimer() {
    if (this.#waitingTimer) clearTimeout(this.#waitingTimer);
    this.#waitingTimer = null;
  }

  /* ----------------------------------------------------------------------- *
   * Helpers
   * ----------------------------------------------------------------------- */

  #readAppliedFromDom() {
    return this.#dedupe([...this.querySelectorAll('[data-tt-code]')].map((el) => el.dataset.ttCode).filter(Boolean));
  }

  /** Codes we wrote this page load if we have them, otherwise whatever Liquid rendered. */
  #currentCodes() {
    return Array.isArray(this.#writtenCodes) ? this.#writtenCodes : this.#readAppliedFromDom();
  }

  /**
   * The codes ACTUALLY on the cart, read from cart.discount_codes.
   *
   * This must be used for anything we write back, because /cart/update.js replaces the
   * whole code set - so a wrong entry in the list silently drops a real discount.
   *
   * The DOM chips cannot be trusted for this. On a fresh page load they are rendered by
   * Liquid from discount_application.title, which for a price-rule-backed discount is
   * the RULE NAME rather than the code. Posting that back wipes the existing discount,
   * which is why codes were being lost intermittently - it only broke when the title
   * and the code differed.
   *
   * Codes are kept even when applicable is false: they are still on the cart, and
   * dropping them here would remove them.
   */
  async #authoritativeCodes(cart) {
    try {
      const source = cart || (await this.#fetchJson(this.#route('cart.js')));

      if (Array.isArray(source.discount_codes)) {
        return this.#dedupe(source.discount_codes.filter((entry) => entry && entry.code).map((entry) => entry.code));
      }
    } catch (error) {
      console.warn('[tt-discount-form] could not read the cart codes, falling back', error);
    }

    return this.#currentCodes();
  }

  #publishCartUpdate(cart) {
    if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    publish(PUB_SUB_EVENTS.cartUpdate, { source: 'tt-discount-form', cartData: cart });
  }

  /** Locale-aware path. The app proxy is deliberately NOT locale-prefixed. */
  #route(path) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return `${root}${path}`.replace(/\/{2,}/g, '/');
  }

  #formatMoney(amount, currency) {
    const value = Number.parseFloat(amount);
    if (!Number.isFinite(value)) return '';

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: currency || 'GBP',
      }).format(value);
    } catch (error) {
      return `${value.toFixed(2)} ${currency || ''}`.trim();
    }
  }

  /** One-time cleanup of keys written by the previous shadow-cart implementation. */
  #purgeLegacyStorage() {
    try {
      if (localStorage.getItem(TT_DISCOUNT_MIGRATION_FLAG)) return;
      TT_DISCOUNT_LEGACY_STORAGE.forEach((key) => localStorage.removeItem(key));
      localStorage.setItem(TT_DISCOUNT_MIGRATION_FLAG, '1');
    } catch (error) {
      // Storage unavailable (private browsing) - nothing to clean up.
    }
  }

  get #maxCodes() {
    return Number.parseInt(this.dataset.maxCodes, 10) || 5;
  }

  get #strings() {
    return {
      empty: this.dataset.stringEmpty,
      max: this.dataset.stringMax,
      duplicate: this.dataset.stringDuplicate,
      unknown: this.dataset.stringUnknown,
      error: this.dataset.stringError,
      timeout: this.dataset.stringTimeout,
      combine: this.dataset.stringCombine,
      applied: this.dataset.stringApplied,
      checking: this.dataset.stringChecking,
      waiting: this.dataset.stringWaiting,
      removing: this.dataset.stringRemoving,
      giftCard: this.dataset.stringGiftCard,
      giftCardPlain: this.dataset.stringGiftCardPlain,
    };
  }
}

if (!customElements.get('tt-discount-form')) {
  customElements.define('tt-discount-form', TTDiscountForm);
}
